import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import MenuCard from '../components/MenuCard';
import CategoryFilter from '../components/CategoryFilter';
import { usePocketBaseRealtime } from '../hooks/usePocketBaseRealtime';
import { dbAdapter } from '../services/dbAdapter';
import { generateOrderCode } from '../utils/excelExport';
import ActiveTablesOverview from '../components/ActiveTablesOverview';
import TableOrderDrawer from '../components/TableOrderDrawer';
import QuickTablePickerModal from '../components/QuickTablePickerModal';

const { FiUser, FiShoppingCart, FiSend, FiCheck, FiHash, FiPlus, FiEye } = FiIcons;

const Cameriere = () => {
  const { data: menu = [], loading: loadingMenu } = usePocketBaseRealtime('menu');
  const { data: comande = [], loading: loadingComande } = usePocketBaseRealtime('comande');

  const [selectedTable, setSelectedTable] = useState(null);
  const [cart, setCart] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('tutte');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [lastOrderCode, setLastOrderCode] = useState('');
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Filtra solo piatti disponibili per l'ordinazione
  const availableMenu = useMemo(() => {
    return menu.filter(piatto => piatto.disponibile === true);
  }, [menu]);

  const categories = useMemo(() => {
    return [...new Set(availableMenu.map(piatto => piatto.categoria))];
  }, [availableMenu]);

  const filteredMenu = useMemo(() => {
    if (selectedCategory === 'tutte') return availableMenu;
    return availableMenu.filter(piatto => piatto.categoria === selectedCategory);
  }, [availableMenu, selectedCategory]);

  // Lista comande attive e tavoli occupati
  const activeComande = useMemo(() => {
    return comande.filter(c => c.status !== 'pagata');
  }, [comande]);

  const activeTableNumbers = useMemo(() => {
    return [...new Set(activeComande.map(c => c.tavolo))];
  }, [activeComande]);

  const addToCart = (piatto) => {
    setCart(prev => ({
      ...prev,
      [piatto.id]: { ...piatto, quantity: (prev[piatto.id]?.quantity || 0) + 1 }
    }));
  };

  const removeFromCart = (piatto) => {
    setCart(prev => {
      const newCart = { ...prev };
      if (newCart[piatto.id]) {
        if (newCart[piatto.id].quantity > 1) {
          newCart[piatto.id].quantity -= 1;
        } else {
          delete newCart[piatto.id];
        }
      }
      return newCart;
    });
  };

  const getTotalItems = () => Object.values(cart).reduce((total, item) => total + item.quantity, 0);
  const getTotalPrice = () => Object.values(cart).reduce((total, item) => total + (item.prezzo * item.quantity), 0);

  const handleSelectTableFromOverview = (tableNum) => {
    setSelectedTable(tableNum);
  };

  const submitOrder = async () => {
    if (!selectedTable || Object.keys(cart).length === 0) return;
    setIsSubmitting(true);
    try {
      const piatti = Object.values(cart).map(item => ({
        nome: item.nome,
        quantita: item.quantity,
        prezzo: item.prezzo
      }));

      const orderCode = generateOrderCode ? generateOrderCode() : Math.floor(1000 + Math.random() * 9000).toString();
      
      await dbAdapter.createComanda({
        tavolo: selectedTable,
        piatti: piatti,
        status: 'attiva',
        codice_comanda: orderCode,
        consegnata: false
      });

      setCart({});
      setLastOrderCode(orderCode);
      setSuccessMessage(`Comanda #${orderCode} inviata istantaneamente in cucina per il Tavolo ${selectedTable}!`);
      
      setTimeout(() => {
        setSuccessMessage('');
        setLastOrderCode('');
      }, 5000);
    } catch (error) {
      console.error('Errore invio comanda:', error);
      alert('Errore durante l\'invio della comanda: ' + (error?.message || 'verifica la connessione'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingMenu && availableMenu.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 flex justify-center h-64 items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5C715E]"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-4 py-8">
      {/* Intestazione Cameriere */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Presa Comande Cameriere</h1>
        <p className="text-base text-gray-600">Seleziona il tavolo, consulta gli ordini attivi e invia nuovi piatti</p>
      </div>

      {/* Messaggio di Successo */}
      {successMessage && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex items-center">
            <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mr-2.5" />
            <span className="text-green-800 font-semibold">{successMessage}</span>
          </div>
        </motion.div>
      )}

      {/* PANORAMICA TAVOLI ATTIVI IN SALA IN TEMPO REALE */}
      <ActiveTablesOverview
        comande={activeComande}
        selectedTable={selectedTable}
        onSelectTable={handleSelectTableFromOverview}
        onOpenNewTable={() => setShowTablePicker(true)}
        mode="cameriere"
      />

      {/* BANNER TAVOLO ATTUALMENTE SELEZIONATO */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-xl bg-[#5C715E] text-white flex items-center justify-center font-bold text-lg">
            {selectedTable || '?'}
          </div>
          <div>
            <div className="text-sm text-gray-500 font-medium">Tavolo Selezionato per l'Ordine:</div>
            <div className="text-2xl font-black text-gray-900">
              {selectedTable ? `Tavolo ${selectedTable}` : 'Nessun tavolo selezionato'}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {selectedTable && activeTableNumbers.includes(selectedTable) && (
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="inline-flex items-center px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition-colors"
            >
              <SafeIcon icon={FiEye} className="w-4 h-4 mr-1.5" />
              Vedi Comande in Corso
            </button>
          )}

          <button
            onClick={() => setShowTablePicker(true)}
            className="inline-flex items-center px-4 py-2.5 bg-[#5C715E] hover:bg-[#4B5C4D] text-white font-semibold rounded-xl text-sm transition-all shadow-sm"
          >
            <SafeIcon icon={FiHash} className="w-4 h-4 mr-1.5" />
            {selectedTable ? 'Cambia Tavolo' : 'Seleziona Tavolo (Numpad)'}
          </button>
        </div>
      </div>

      {/* SEZIONE MENU PIATTI DISPONIBILI */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Menù Disponibile</h2>
          <span className="text-xs text-gray-500">{filteredMenu.length} piatti pronti per l'ordine</span>
        </div>

        <CategoryFilter categories={categories} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {filteredMenu.map((piatto) => (
            <MenuCard 
              key={piatto.id} 
              piatto={piatto} 
              onAdd={addToCart} 
              onRemove={removeFromCart} 
              quantity={cart[piatto.id]?.quantity || 0} 
              showControls={true} 
            />
          ))}
        </div>
      </div>

      {/* CARRELLO SOSPESO FLOTTANTE */}
      {Object.keys(cart).length > 0 && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-6 right-6 left-6 md:left-auto md:w-96 bg-white rounded-2xl p-6 shadow-2xl border border-gray-200 z-40">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center justify-between">
            <span className="flex items-center"><SafeIcon icon={FiShoppingCart} className="mr-2 text-[#5C715E]" /> Nuova Comanda</span>
            <span className="bg-[#5C715E] text-white text-xs px-2.5 py-1 rounded-full font-bold">{getTotalItems()}</span>
          </h2>

          <div className="max-h-60 overflow-y-auto mb-4 space-y-2 pr-1">
            {Object.values(cart).map((item) => (
              <div key={item.id} className="flex justify-between text-sm border-b pb-2">
                <span>{item.nome} x{item.quantity}</span>
                <span className="font-bold">€{(item.prezzo * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="flex justify-between font-black text-lg mb-4 text-gray-900">
            <span>Totale:</span>
            <span className="text-emerald-700">€{getTotalPrice().toFixed(2)}</span>
          </div>

          <button 
            onClick={submitOrder} 
            disabled={!selectedTable || isSubmitting}
            className="w-full bg-[#5C715E] text-white py-3.5 rounded-xl font-bold hover:bg-[#4B5C4D] disabled:opacity-50 transition-all shadow-lg flex items-center justify-center text-base active:scale-95"
          >
            <SafeIcon icon={FiSend} className="mr-2" />
            {isSubmitting ? 'Invio in corso...' : `Invia in Cucina (Tavolo ${selectedTable || '?'})`}
          </button>
        </motion.div>
      )}

      {/* DRAWER DETTAGLIO COMANDA ESISTENTE */}
      <TableOrderDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        tableNumber={selectedTable}
        comande={activeComande}
        onAddMoreDishes={() => setIsDrawerOpen(false)}
        mode="cameriere"
      />

      {/* NUMPAD MODAL PER APRIRE NUOVO TAVOLO */}
      <QuickTablePickerModal
        isOpen={showTablePicker}
        onClose={() => setShowTablePicker(false)}
        onSelectTable={(num) => setSelectedTable(num)}
        activeTables={activeTableNumbers}
      />
    </motion.div>
  );
};

export default Cameriere;